import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'apollo-client-ppc';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Checkbox from '@atlaskit/checkbox';
import { ValueType } from '@atlaskit/select';
import styled from '@emotion/styled';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { DISCOUNT_LABELS } from '@css-xp/base';
import { CartCustomerSearchItem } from '../../customer/src/types';
import {
  FetchCustomerBillingProfilesForTxaInPpcQuery,
  FetchCustomerBillingProfilesForTxaInPpcQueryVariables,
  InvoiceGroup,
  PaymentMethodType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { fetchCustomerBillingProfilesForTxaInPPC } from '../../order-summary/src/queries/fetch-billing-profiles-for-transaction-account';
import { defaultBillingProfileSortOptions, maxThresholdForResults } from '@css-xp/ppc-constants';
import { sortBillingProfiles } from '@css-xp/ppc-utils';
import { BillingProfileOption } from '../../order-summary/src/components/order-summary';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { ChangeBillingProfile } from '../../order-summary/src/components/change-billing-profile';

interface Props {
  onClose: () => {};
  isModalOpen: boolean;
  invoiceGroupId: string | null;
  transactionAccountId: string;
  customer: CartCustomerSearchItem;
  onSubmit: (billingProfileId: string, isPartnerMarginIncluded: boolean) => void;
  isPaypalEnabled?: boolean;
}

const partnerDiscountsText = DISCOUNT_LABELS.PARTNER_DISCOUNT.toLowerCase() + 's';

enum SCREEN_NAME {
  BILLING_PROFILE,
  PARTNER_MARGIN,
}

const SubmitButtonWrapper = styled.div(() => ({
  width: '85px',
  textAlign: 'right',
}));

const ModalFooterWrapper = styled.div(() => ({
  width: '100%',
}));

const LoadingIndicatorWrapper = styled.div(() => ({
  position: 'relative',
  minHeight: '200px',
}));
export const EstimateModalWrapper = ({
  isModalOpen,
  onClose,
  transactionAccountId,
  invoiceGroupId,
  customer,
  onSubmit,
  isPaypalEnabled = false,
}: Props) => {
  const [isPartnerMarginIncluded, setIsPartnerMarginIncluded] = React.useState<boolean>(true);
  const [selectedBillingProfile, setSelectedBillingProfile] = useState<ValueType<BillingProfileOption> | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [screenValues] = useState([SCREEN_NAME.BILLING_PROFILE, SCREEN_NAME.PARTNER_MARGIN]);

  const { data: billingProfilesForCustomer, loading } = useQuery<
    FetchCustomerBillingProfilesForTxaInPpcQuery,
    FetchCustomerBillingProfilesForTxaInPpcQueryVariables
  >(fetchCustomerBillingProfilesForTxaInPPC, {
    variables: {
      id: transactionAccountId,
      filterOptions: [
        {
          key: 'or',
          // sending multiple ship-to ids
          value: JSON.stringify(customer?.ids?.map((shipToId) => ({ shipToId }))),
        },
      ],
      searchOptions: {
        first: maxThresholdForResults,
        offset: 0,
        sort: defaultBillingProfileSortOptions,
      },
    },
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network',
    skip: !transactionAccountId || !customer?.ids || !customer?.ids.length,
  });
  const renderPartnerMarginStep = () => {
    return (
      <div>
        {`By default, the estimate PDF/JSON doesn't include ${partnerDiscountsText} or billing profile. Select the checkbox to include them.`}
        <div className="partner-margin-checkbox">
          <Checkbox
            label={`Include ${partnerDiscountsText}`}
            isChecked={isPartnerMarginIncluded}
            onChange={() => {
              setIsPartnerMarginIncluded((prevValue) => !prevValue);
            }}
            testId="partner-margin-checkbox"
          />
        </div>
      </div>
    );
  };

  const availableBillingProfilesForCustomer = (billingProfilesForCustomer?.transactionAccount?.billingProfiles?.nodes ??
    []) as InvoiceGroup[];

  // selecting default billing profile
  useEffect(() => {
    const billingProfileObj = availableBillingProfilesForCustomer.find(
      (billingProfile) => billingProfile.id === invoiceGroupId
    );
    if (billingProfileObj && billingProfileObj.name && billingProfileObj.id) {
      setSelectedBillingProfile({
        label: billingProfileObj.name,
        value: billingProfileObj.id,
      });
    }
  }, [availableBillingProfilesForCustomer, invoiceGroupId]);

  let filteredBillingProfiles = availableBillingProfilesForCustomer;
  /* we're using this sorting because the response from api is ASCII sorted, so we need to sort it in a case-insensitive way.
   * This is a temporary solution, and we are only using it here since we are fetching all the billing profiles in one go.
   */
  filteredBillingProfiles = sortBillingProfiles(filteredBillingProfiles, true, true);
  filteredBillingProfiles = filteredBillingProfiles?.filter((billingProfile) => billingProfile?.isActive === true);

  const handleNextButton = () => {
    if (selectedIndex === SCREEN_NAME.BILLING_PROFILE) {
      setSelectedIndex(SCREEN_NAME.PARTNER_MARGIN);
      return;
    }

    if (selectedBillingProfile) {
      resetModal();
      onSubmit(selectedBillingProfile.value, isPartnerMarginIncluded);
    }
  };

  const resetModal = () => {
    setIsPartnerMarginIncluded(true);
    setSelectedIndex(SCREEN_NAME.BILLING_PROFILE);
  };

  const onCloseModal = () => {
    resetModal();
    onClose();
  };

  const isSubmitButtonDisabled = useMemo(() => {
    if (selectedIndex === SCREEN_NAME.BILLING_PROFILE) {
      return !selectedBillingProfile || loading;
    }
  }, [selectedIndex, loading, selectedBillingProfile]);

  return (
    <ModalTransition>
      {isModalOpen && (
        <Modal onClose={onCloseModal} testId="partner-margin-dialog-content">
          <ModalHeader>
            <ModalTitle>Generate estimate</ModalTitle>
            <Button
              appearance="subtle"
              onClick={onCloseModal}
              label="Close Modal"
              iconBefore={<CrossIcon label="Close Modal" />}
            />
          </ModalHeader>
          <ModalBody>
            {selectedIndex === SCREEN_NAME.BILLING_PROFILE ? (
              <div>
                <p className="mb-8">
                  Select a billing profile for this estimate? You can always change it on the Order Summary if needed.
                </p>
                {loading ? (
                  <LoadingIndicatorWrapper>
                    <LoadingIndicator />
                  </LoadingIndicatorWrapper>
                ) : (
                  <ChangeBillingProfile
                    billingProfilesInfo={filteredBillingProfiles}
                    selectedBillingProfile={selectedBillingProfile}
                    setSelectedBillingProfile={(selectedBillingProfileForEstimate) => {
                      if (selectedBillingProfileForEstimate) {
                        setSelectedBillingProfile(selectedBillingProfileForEstimate);
                      }
                    }}
                    isDeferredPaymentMethodDisabled={true}
                    isNoPaymentMethodDisabled={true}
                    isPaypalEnabled={isPaypalEnabled}
                  />
                )}
              </div>
            ) : (
              renderPartnerMarginStep()
            )}
          </ModalBody>
          <ModalFooter>
            <ModalFooterWrapper className="flex align-center justify-space-between">
              <Button
                appearance="subtle"
                onClick={() => {
                  setSelectedIndex(SCREEN_NAME.BILLING_PROFILE);
                }}
                isDisabled={selectedIndex === SCREEN_NAME.BILLING_PROFILE}
              >
                Previous
              </Button>
              <ProgressIndicator selectedIndex={selectedIndex} values={screenValues} />
              <SubmitButtonWrapper>
                <Button
                  appearance="primary"
                  onClick={handleNextButton}
                  testId="next-button"
                  isDisabled={isSubmitButtonDisabled}
                >
                  {selectedIndex === SCREEN_NAME.BILLING_PROFILE ? 'Next' : 'Generate'}
                </Button>
              </SubmitButtonWrapper>
            </ModalFooterWrapper>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
